import { Box, Popover, TextField } from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { Dayjs } from 'dayjs';
import { useState } from 'react';
import zeplinColor from '../../../theme/Color';

type CustomDateRangePickerProps = {
  startDate: Dayjs | null;
  closeDate: Dayjs | null;
  onStartDateChange: (date: Dayjs | null) => void;
  onCloseDateChange: (date: Dayjs | null) => void;
  label?: string;
  placeholder?: string;
  required?: boolean;
  size?: 'small' | 'medium';
};

const CustomDateRangePicker = ({
  startDate,
  closeDate,
  onStartDateChange,
  onCloseDateChange,
  label = 'Close date',
  placeholder = 'Close date',
  required = false,
  size = 'medium',
}: CustomDateRangePickerProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
  const open = Boolean(anchorEl);

  const formatDateRange = () => {
    if (startDate && closeDate) {
      return `${startDate.format('MM/DD/YY')} - ${closeDate.format('MM/DD/YY')}`;
    } else if (startDate) {
      return `${startDate.format('MM/DD/YY')} - `;
    } else if (closeDate) {
      return ` - ${closeDate.format('MM/DD/YY')}`;
    }
    return '';
  };

  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box>
        <TextField
          label={label}
          placeholder={placeholder}
          value={formatDateRange()}
          onClick={handleClick}
          required={required}
          size={size}
          fullWidth
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: '14px',
              '& .MuiInputAdornment-root': {
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
              },
            },
            '& .MuiFormLabel-asterisk': { color: zeplinColor.Error },
          }}
        />

        <Popover
          open={open}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          sx={{
            '& .MuiPaper-root': {
              borderRadius: '14px',
              padding: '16px',
              boxShadow: 'rgba(0, 0, 0, 0.15)',
            },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
              width: '100%',
              minWidth: '300px',
            }}
          >
            <DatePicker
              label='Start date'
              sx={{
                width: '100%',
                '& .MuiFormLabel-asterisk': { color: zeplinColor.Error },
                '& .MuiOutlinedInput-root': {
                  borderRadius: '14px',
                  '& .MuiInputAdornment-root': {
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100%',
                  },
                  '& .MuiIconButton-root': {
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  },
                },
              }}
              slotProps={{
                textField: {
                  required: false,
                  size: size ? size : 'medium',
                },
                inputAdornment: {
                  sx: {
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  },
                },
              }}
              value={startDate || null}
              onChange={(newValue) => onStartDateChange(newValue)}
              slots={{
                openPickerIcon: () => (
                  <img
                    src={'/assets/calendar.svg'}
                    width={19}
                    height={19}
                    style={{ display: 'flex', alignItems: 'center' }}
                  />
                ),
              }}
            />

            <DatePicker
              label='Close date'
              sx={{
                width: '100%',
                '& .MuiFormLabel-asterisk': { color: zeplinColor.Error },
                '& .MuiOutlinedInput-root': {
                  borderRadius: '14px',
                  '& .MuiInputAdornment-root': {
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100%',
                  },
                  '& .MuiIconButton-root': {
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  },
                },
              }}
              slotProps={{
                textField: {
                  required: false,
                  size: size ? size : 'medium',
                },
                inputAdornment: {
                  sx: {
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  },
                },
              }}
              value={closeDate || null}
              onChange={(newValue) => onCloseDateChange(newValue)}
              slots={{
                openPickerIcon: () => (
                  <img
                    src={'/assets/calendar.svg'}
                    width={19}
                    height={19}
                    style={{ display: 'flex', alignItems: 'center' }}
                  />
                ),
              }}
            />
          </Box>
        </Popover>
      </Box>
    </LocalizationProvider>
  );
};

export default CustomDateRangePicker;
